import type { Substance } from '../types';

/**
 * The body's own signalling molecules.
 *
 * These use the direct-release pharmacokinetic model: they are not swallowed, so
 * the dose slider represents a release/synthesis level in nanomolar rather than
 * an oral dose. Real synaptic transmitters are also released and cleared within
 * milliseconds to seconds — far faster than an hours-scale slider can depict —
 * so the timescale here is stretched for interactivity and is not literal
 * synaptic timing. Circulating hormones (cortisol, melatonin, adrenaline) do
 * genuinely operate on a minutes-to-hours scale.
 */

export const dopamine: Substance = {
  id: 'dopamine',
  displayName: 'Dopamine',
  emoji: '✨',
  aliases: ['dopamine'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'Central to reward, motivation and movement. D1-family receptors have notably lower affinity for ' +
    'dopamine than D2, and D2 binds it in two distinct affinity states — real, well-documented complexity ' +
    'that this model simplifies to one value per receptor.',
  pk: { route: 'direct-release', ke: 1.0 },
  doseRange: { min: 5, max: 100, default: 30, step: 5, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: 'D2',
      kiNM: 5,
      action: 'agonist',
      citation:
        'High-affinity-state estimate. D2 binds dopamine in two states (high ~1-10 nM, low ~1-2 uM); this ' +
        'model uses only the high-affinity figure. Richfield EK et al. (1989) Neuroscience 30:767-777. PMID: 2528080.',
    },
    {
      receptor: 'D1',
      kiNM: 1700,
      action: 'agonist',
      citation:
        'Order-of-magnitude estimate. D1 genuinely requires much higher ("phasic") dopamine to activate; a ' +
        'single universal Ki was not confirmed, so treat as illustrative of that lower affinity. ' +
        'Missale C et al. (1998) Physiol Rev 78:189-225. PMID: 9457173.',
    },
    {
      receptor: 'D3',
      kiNM: 27,
      action: 'agonist',
      citation: 'Approximate; D3 has relatively high dopamine affinity. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ffcc00', secondary: '#ffaa00', particleSpeedMultiplier: 1.3 },
};

export const serotonin: Substance = {
  id: 'serotonin',
  displayName: 'Serotonin',
  emoji: '🌊',
  aliases: ['serotonin', '5-ht', '5ht'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'Acts broadly across 5-HT receptor subtypes in mood and emotional regulation. Worth knowing: roughly ' +
    '90% of the body\'s serotonin is in the gut, not the brain, where it regulates motility — which is ' +
    'why serotonergic drugs so often have gastrointestinal effects.',
  pk: { route: 'direct-release', ke: 1.0 },
  doseRange: { min: 5, max: 100, default: 30, step: 5, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: '5HT1A',
      kiNM: 1,
      action: 'agonist',
      citation:
        'Approximate, from high-affinity-site binding studies used as a proxy for serotonin\'s own ' +
        'affinity; a primary citation specific to serotonin (rather than a synthetic agonist) was not ' +
        'confirmed. Order-of-magnitude estimate.',
    },
    {
      receptor: '5HT2A',
      kiNM: 10,
      action: 'agonist',
      citation:
        'Approximate high-affinity-state value under the standard two-state model (~10 nM high vs ' +
        '~250-360 nM low). Illustrative of the pattern rather than an exact constant.',
    },
    {
      receptor: '5HT3',
      kiNM: 600,
      action: 'agonist',
      citation: 'Approximate; 5-HT3 mediates nausea and gut motility. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#44ccdd', secondary: '#3399bb', particleSpeedMultiplier: 0.8 },
};

export const adrenaline: Substance = {
  id: 'adrenaline',
  displayName: 'Adrenaline',
  emoji: '⚡',
  aliases: ['adrenaline', 'epinephrine'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'The fight-or-flight hormone, released from the adrenal glands into the bloodstream. Its action is ' +
    'overwhelmingly PERIPHERAL — it barely crosses the blood-brain barrier. The racing heart and shaking ' +
    'hands of fear are adrenaline acting on the body; the fear itself is generated separately in the brain.',
  pk: { route: 'direct-release', ke: 12.0 },
  doseRange: { min: 1, max: 60, default: 20, step: 1, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: 'BETA1',
      kiNM: 30,
      action: 'agonist',
      citation: 'Approximate; adrenaline is a potent non-selective adrenergic agonist. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'BETA2',
      kiNM: 30,
      action: 'agonist',
      citation: 'Approximate. NIMH PDSP Ki Database, https://pdsp.unc.edu/databases/kidb.php.',
    },
    {
      receptor: 'ALPHA1',
      kiNM: 100,
      action: 'agonist',
      citation: 'Approximate; drives vasoconstriction. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ff3344', secondary: '#ff8800', particleSpeedMultiplier: 2.5 },
};

export const acetylcholine: Substance = {
  id: 'acetylcholine',
  displayName: 'Acetylcholine',
  emoji: '🔗',
  aliases: ['acetylcholine', 'ach'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'The transmitter of every neuromuscular junction and every autonomic ganglion, plus a major brain ' +
    'transmitter for attention and memory. Nothing else in the body has a job description this broad — ' +
    'without it, no voluntary muscle would move at all.',
  pk: { route: 'direct-release', ke: 20.0 },
  doseRange: { min: 5, max: 120, default: 40, step: 5, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: 'nAChR_MUSCLE',
      kiNM: 100,
      action: 'agonist',
      citation:
        'Approximate; muscle-type nAChR affinity for its native transmitter. Order-of-magnitude estimate.',
    },
    {
      receptor: 'nAChR',
      kiNM: 60,
      action: 'agonist',
      citation: 'Approximate, neuronal a4b2. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'M1',
      kiNM: 40,
      action: 'agonist',
      citation: 'Approximate. NIMH PDSP Ki Database.',
    },
    {
      receptor: 'M2',
      kiNM: 40,
      action: 'agonist',
      citation: 'Approximate; M2 mediates vagal slowing of the heart. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#88ffdd', secondary: '#44bbaa', particleSpeedMultiplier: 1.5 },
};

export const endorphin: Substance = {
  id: 'endorphin',
  displayName: 'Endorphins',
  emoji: '🌟',
  aliases: ['endorphin', 'endorphins', 'beta-endorphin'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'The body\'s own opioid peptides, acting at the same μ-receptors as morphine. Released during exercise, ' +
    'stress and injury, they blunt pain at the spinal cord and produce mild euphoria — the reason a serious ' +
    'injury can go briefly unnoticed.',
  pk: { route: 'direct-release', ke: 2.5 },
  doseRange: { min: 1, max: 50, default: 15, step: 1, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: 'MOR',
      kiNM: 1.0,
      action: 'agonist',
      citation:
        'Approximate; beta-endorphin has high mu-opioid affinity comparable to morphine. ' +
        'Order-of-magnitude estimate.',
    },
    {
      receptor: 'DOR',
      kiNM: 3.0,
      action: 'agonist',
      citation: 'Approximate. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ffeedd', secondary: '#ffccaa', particleSpeedMultiplier: 1.1 },
};

export const cortisol: Substance = {
  id: 'cortisol',
  displayName: 'Cortisol',
  emoji: '😰',
  aliases: ['cortisol', 'stress', 'hydrocortisone'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'The main stress hormone. Unlike fast transmitters it acts on a nuclear receptor, changing gene ' +
    'expression over hours rather than milliseconds. Sustained elevation is associated with hippocampal ' +
    'changes, which is one proposed link between chronic stress and memory problems.',
  pk: { route: 'direct-release', ke: 0.42 },
  doseRange: { min: 50, max: 800, default: 200, step: 25, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: 'GR',
      kiNM: 20,
      action: 'agonist',
      citation:
        'Approximate glucocorticoid receptor affinity. Nuclear receptor acting on gene transcription, so ' +
        'the occupancy-to-effect relationship differs fundamentally from the ion channels and GPCRs ' +
        'elsewhere in this model.',
    },
  ],
  colorPalette: { primary: '#ff7744', secondary: '#cc4422', particleSpeedMultiplier: 1.4 },
};

export const histamine: Substance = {
  id: 'histamine',
  displayName: 'Histamine',
  emoji: '🐝',
  aliases: ['histamine'],
  substanceClass: 'endogenous',
  mechanismSummary:
    'Best known for allergy, but in the brain it is a wakefulness transmitter released from the ' +
    'hypothalamus. That dual role is why antihistamines that cross into the brain cause sleepiness while ' +
    'newer ones that stay peripheral do not.',
  pk: { route: 'direct-release', ke: 6.0 },
  doseRange: { min: 5, max: 150, default: 40, step: 5, unit: 'nM', displayScale: 1 },
  bindings: [
    {
      receptor: 'H1',
      kiNM: 8,
      action: 'agonist',
      citation: 'Approximate native-ligand affinity. NIMH PDSP Ki Database.',
    },
  ],
  colorPalette: { primary: '#ffdd66', secondary: '#ddaa33', particleSpeedMultiplier: 1.3 },
};

export const ENDOGENOUS = [
  dopamine,
  serotonin,
  adrenaline,
  acetylcholine,
  endorphin,
  cortisol,
  histamine,
];
